import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
  RoutesRecognized
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { GlobalFunctionsService } from './core/services/global-functions.service';
import { PaymentService } from './core/services/payment.service';
import { AppConfig } from './app.config';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  subscription: Subscription;
  subscriptionActiveTab: Subscription;
  subscriptionShowBasket: Subscription;
  subscriptionShowTabs: Subscription;
  enabledTabCount = 1;
  showBasket = true;
  showTabs = true;
  language: string;
  loading: boolean;

  get logoUrl(): string {
    return this.config.getConfig('logoUrl');
  }

  get isMultilanguage(): boolean {
    return this.config.getConfig('multilanguage');
  }

  constructor(
    private globalFunctionsService: GlobalFunctionsService,
    private paymentService: PaymentService,
    private router: Router,
    private translate: TranslateService,
    private config: AppConfig
  ) {
    this.translate.setDefaultLang('nl');
    this.translate.use('nl');
    this.language = this.translate.currentLang;

    this.loading = true;
    router.events.subscribe((event: RouterEvent) => {
      this.navigationInterceptor(event);
    });

    this.subscription = this.globalFunctionsService
      .enabledTabsChanged()
      .subscribe(enabledTabCount => (this.enabledTabCount = enabledTabCount));
    this.subscriptionShowBasket = this.globalFunctionsService
      .showBasketChanged()
      .subscribe(showBasket => (this.showBasket = showBasket));
    this.subscriptionShowTabs = this.globalFunctionsService
      .showTabsChanged()
      .subscribe(showTabs => (this.showTabs = showTabs));
  }

  ngOnInit(): void {
    const colorPrimary = this.config.getConfig('colorPrimary');
    const colorSecondary = this.config.getConfig('colorSecondary');
    if (colorPrimary && colorSecondary) {
      const css = `:root {
        --color-primary: ${this.hexToRGB(colorPrimary)};
        --color-primary-light: ${this.hexToRGB(colorPrimary, 0.14)};
        --color-secondary: ${this.hexToRGB(colorSecondary)};
        --color-secondary-light: ${this.hexToRGB(colorSecondary, 0.12)};
        --color-background: ${this.hexToRGB(colorSecondary, 0.04)};
      }`;
      const head = document.getElementsByTagName('head')[0];
      const style = document.createElement('style');
      style.appendChild(document.createTextNode(css));
      head.appendChild(style);
    }

    this.paymentService.getClientIp();

    this.router.events.subscribe(route => {
      if (route instanceof RoutesRecognized) {
        // Get ReturnUrl
        const returnUrl = route.state.root.queryParams['returnurl'];
        if (returnUrl != null) {
          this.globalFunctionsService.returnUrl = returnUrl;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.subscriptionActiveTab.unsubscribe();
    this.subscriptionShowBasket.unsubscribe();
  }

  navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.loading = true;
    }
    if (event instanceof NavigationEnd) {
      this.loading = false;
    }
    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this.loading = false;
    }
    if (event instanceof NavigationError) {
      this.loading = false;
    }
  }

  selectTab(): void {
    // Jump to top of page on tab-switch.
    window.scrollTo(0, 0);
  }

  useLanguage(language: string): void {
    this.language = language;
    this.translate.use(language);
  }

  private hexToRGB(hex: string, opacity: number = 1): string {
    let r = '0';
    let g = '0';
    let b = '0';

    if (hex.length === 4) {
      r = '0x' + hex[1] + hex[1];
      g = '0x' + hex[2] + hex[2];
      b = '0x' + hex[3] + hex[3];
    } else if (hex.length === 7) {
      r = '0x' + hex[1] + hex[2];
      g = '0x' + hex[3] + hex[4];
      b = '0x' + hex[5] + hex[6];
    }

    return 'rgba(' + +r + ',' + +g + ',' + +b + ',' + opacity + ')';
  }
}
